import {AccountId, BaseCurrencyCode, Resource} from '@raison/types';
import {createSelector} from '@reduxjs/toolkit';
import {Currency, MoneyAmount, MoneyFormat} from '~/library/money-kit';
import {selectAssetsResource} from '~/redux/assets/assetsResource';

export const selectCurrentCurrency = (store: AppModel): BaseCurrencyCode => {
    return store.assets.currentCurrency;
};

export const selectAssetsLoadingFlag = (store: AppModel) => {
    return store.assets.fetching;
};

export const selectAssetsData = (store: AppModel) => {
    return store.assets.assets;
};

export const selectAllAssetsAccounts = (state: AppModel) => {
    return Object.values(state.assets.assets?.accounts ?? {}).flat();
};

export const selectAssets = (store: AppModel) => {
    return store.assets;
};

export const selectAssetsTotalBalance = (state: AppModel) =>
    selectAssetsResource(state).mapSuccess(({total, accounts}): Resource.Or<string> => {
        const primaryCurrency = selectCurrentCurrency(state);
        const amount = total[primaryCurrency];
        if (typeof amount !== 'number') return Resource.from({isError: true});

        const decimals = Object.values(accounts)
            .flat()
            .find(account => account.currency === primaryCurrency)!.decimals_assets;

        return MoneyFormat.from({code: primaryCurrency, decimals: decimals}).formatWithSymbol(amount);
    });

export const selectBlockedAmountFor = (coinFrom: string) =>
    createSelector(selectAccountFor(coinFrom), coinAccount => {
        if (!coinAccount) {
            return null;
        }
        const isForCryptoCoin = coinAccount.currency === 'BTC' || coinAccount.currency == 'ETH';
        if (!isForCryptoCoin) {
            return null;
        }
        const amount = MoneyAmount.from(coinAccount.balance_locked, Currency.from(coinFrom, coinAccount.decimals));
        if (amount.gt(0)) {
            return MoneyFormat.from(amount).formatWithCode();
        } else {
            return null;
        }
    });

export const selectBlockedAmountById = (id: AccountId) =>
    createSelector(selectAccountById(id), coinAccount => {
        if (!coinAccount) {
            return null;
        }
        const isForCryptoCoin = coinAccount.currency === 'BTC' || coinAccount.currency == 'ETH';
        if (!isForCryptoCoin) {
            return null;
        }
        const amount = MoneyAmount.from(
            coinAccount.balance_locked,
            Currency.from(coinAccount.currency, coinAccount.decimals)
        );
        if (amount.gt(0)) {
            return MoneyFormat.from(amount).formatWithCode();
        } else {
            return null;
        }
    });

const selectAccountFor = (coinFrom: string) => (state: AppModel) =>
    selectAssetsData(state)?.accounts.crypto.find(acc => acc.currency === coinFrom);

const selectAccountById = (id: AccountId) => (state: AppModel) =>
    selectAssetsData(state)?.accounts.crypto.find(acc => acc.id === id);
